import {
  BooleanField,
  BooleanInput,
  BulkDeleteButton,
  ChipField,
  Create,
  Datagrid,
  DateField,
  DateTimeInput,
  Edit,
  List,
  Loading,
  Resource,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
  useRecordContext,
} from "react-admin";
import { CrudPermissions } from "../../auth/crud-permissions";
import {
  ConversionOutputStreamDestination,
  ConversionOutputStreamResource,
  getSensitiveFieldsForDestination,
  omitUnchangedSensitiveFields,
  OpenAiAdsEventType,
  OpenAiAdsEventTypes,
  outputStreamAdAccountPlatform,
  OutputStreamStatsEntry,
  StartFrom,
} from "@arrowsup/platform-dtos";
import { AdAccountInput } from "../../ad-platforms/ad-account-input";
import { useConversionFilters } from "../../../logic/conversions/use-conversion-filter";
import { useOutputStreamStats } from "../../../logic/conversions/use-output-stream-stats";
import OutputIcon from "@mui/icons-material/Output";
import { Box, Divider, Typography } from "@mui/material";
import { useWatch } from "react-hook-form";
import { omit } from "../../../logic/omit";
import { createContext, ReactElement } from "react";
import * as ss from "superstruct";
import {
  formatTimestamp,
  parseTimestamp,
} from "../../../logic/api/timestamp-codec";
import { TopHelpInfoText } from "../../help/top-help-info-text";
import { createStatsField } from "../shared/stats-field";

type OutputStreamStatsContextValue = {
  statsMap: Map<number, OutputStreamStatsEntry>;
  isLoading: boolean;
};

const OutputStreamStatsContext = createContext<OutputStreamStatsContextValue>({
  statsMap: new Map(),
  isLoading: true,
});

const StatsWindow = ss.enums(["stats1d", "stats7d", "stats30d"]);

const StatsField = createStatsField(
  OutputStreamStatsContext,
  (entry, window) => {
    const stats = ss.is(window, StatsWindow) ? entry[window] : undefined;

    if (!stats || stats.total === 0) return null;
    const allSuccess = stats.success === stats.total;

    return {
      label: `${Number(stats.success).toLocaleString()}/${Number(
        stats.total
      ).toLocaleString()}`,
      color: allSuccess ? "secondary" : "primary",
    };
  }
);

export const ConversionOutputStream = (perms: CrudPermissions) => {
  return (
    <Resource
      name="conversion-output-stream"
      key="conversion-output-stream"
      options={{
        label: "Output Streams",
      }}
      list={ConversionOutputStreamList(perms)}
      create={perms.create ? ConversionOutputStreamCreate() : undefined}
      edit={perms.update ? ConversionOutputStreamEdit() : undefined}
      icon={OutputIcon}
    />
  );
};

const ConversionOutputStreamListInner: React.FC<{
  perms: CrudPermissions;
}> = ({ perms }) => {
  const stats = useOutputStreamStats();

  return (
    <OutputStreamStatsContext.Provider value={stats}>
      <TopHelpInfoText text="Send filtered conversions to external destinations like Google Ads, Meta, OpenAI, or webhooks." />
      <List>
        <Datagrid
          rowClick={perms.update ? "show" : false}
          bulkActionButtons={perms.delete ? <BulkDeleteButton /> : false}
        >
          <TextField source="id" label="Stream ID" />
          <TextField source="description" label="Description" />
          <ChipField source="destination.name" label="Destination" />
          <TextField
            source="adAccount.externalId"
            label="Ad Account"
            emptyText="—"
            sortable={false}
          />
          <TextField source="filter.name" label="Filter" />
          <ChipField source="startFrom" label="Start From" />
          <BooleanField source="enabled" />
          <BooleanField source="realtime" />
          <StatsField
            source="id"
            window="stats1d"
            label="1d"
            sortable={false}
          />
          <StatsField
            source="id"
            window="stats7d"
            label="7d"
            sortable={false}
          />
          <StatsField
            source="id"
            window="stats30d"
            label="30d"
            sortable={false}
          />
          <DateField source="createdAt" label="Created" />
          <DateField source="updatedAt" label="Last Updated" />
        </Datagrid>
      </List>
    </OutputStreamStatsContext.Provider>
  );
};

export const ConversionOutputStreamList = (perms: CrudPermissions) => {
  return <ConversionOutputStreamListInner perms={perms} />;
};

const FilterSelectInput: React.FC = () => {
  // Load saved filters.
  const { data, isPending, error } = useConversionFilters();

  if (isPending) {
    return <Loading />;
  }

  if (error) {
    throw new Error(`Error loading conversion filters: ${error.message}`);
  }

  return (
    <SelectInput
      source="filter.id"
      choices={data.data.map((filter) => ({
        id: filter.id,
        name: filter.name,
      }))}
      required
      fullWidth
    />
  );
};

const StartFromInput = () => {
  return (
    <SelectInput
      source="startFrom"
      choices={[
        { id: "beginning" satisfies StartFrom, name: "Beginning" },
        { id: "timestamp" satisfies StartFrom, name: "Timestamp" },
        { id: "end" satisfies StartFrom, name: "End" },
      ]}
      defaultValue={"end" satisfies StartFrom}
      required
      fullWidth
    />
  );
};

const FromTimestampInput = () => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const startFrom: StartFrom | undefined = useWatch({ name: "startFrom" });
  const record = useRecordContext();
  const isNew = record?.id === undefined;

  return (
    <DateTimeInput
      label="From Timestamp"
      source="fromTimestampMs"
      parse={parseTimestamp}
      format={formatTimestamp}
      helperText="The created timestamp (not event time) to start from, if 'Timestamp' is selected. Cannot be edited after the stream is created."
      fullWidth
      disabled={startFrom !== "timestamp" || !isNew}
    />
  );
};

const DestinationChooser: React.FC = () => {
  return (
    <SelectInput
      source="destination.name"
      choices={[
        {
          id: "email" satisfies ConversionOutputStreamDestination,
          name: "Email",
        },
        {
          id: "google-ads-data-manager" satisfies ConversionOutputStreamDestination,
          name: "Google Ads (Data Manager API)",
        },
        {
          id: "meta-ads" satisfies ConversionOutputStreamDestination,
          name: "Meta Ads",
        },
        {
          id: "microsoft-ads-conversion" satisfies ConversionOutputStreamDestination,
          name: "Microsoft Ads Conversion",
        },
        {
          id: "openai-ads" satisfies ConversionOutputStreamDestination,
          name: "OpenAI Ads",
        },
        {
          id: "webhook" satisfies ConversionOutputStreamDestination,
          name: "Webhook",
        },
      ]}
      required
      fullWidth
    />
  );
};

/** The ad account picker for `destination`, which must send to one. */
const DestinationAdAccountInput: React.FC<{
  destination: ConversionOutputStreamDestination;
}> = ({ destination }) => {
  const platform = outputStreamAdAccountPlatform(destination);

  return platform ? <AdAccountInput platform={platform} /> : null;
};

const NoDestinationSet: React.FC = () => {
  return <Typography>No destination set.</Typography>;
};

const EmailDestinationConfig: React.FC = () => {
  return (
    <TextInput
      label="Destination Email Address"
      source="destination.to"
      fullWidth
    />
  );
};

const WebhookDestinationConfig: React.FC = () => {
  return (
    <>
      <TextInput
        label="Destination URL"
        source="destination.webhookUrl"
        fullWidth
      />
    </>
  );
};

const MetaAdsDestinationConfig: React.FC = () => {
  return (
    <>
      <TextInput
        label="Meta Access Token"
        source="destination.accessToken"
        type="password"
        helperText="Write-only — will show as masked when saved."
        fullWidth
      />
      <TextInput label="Meta Pixel ID" source="destination.pixelId" fullWidth />
      <TextInput
        label="Meta Event Name"
        source="destination.eventName"
        fullWidth
      />
      <DestinationAdAccountInput destination="meta-ads" />
    </>
  );
};

const GoogleAdsDataManagerDestinationConfig: React.FC = () => {
  return (
    <>
      <TextInput
        label="Google Ads Conversion Action ID (numeric)"
        source="destination.conversionActionId"
        fullWidth
      />
      <BooleanInput label="Enhanced" source="destination.enhanced" fullWidth />
      <DestinationAdAccountInput destination="google-ads-data-manager" />
    </>
  );
};

const MicrosoftAdsConversionDestinationConfig: React.FC = () => {
  return (
    <>
      <TextInput
        label="Microsoft Ads Conversion Name"
        source="destination.conversionName"
        fullWidth
      />
      <BooleanInput label="Enhanced" source="destination.enhanced" fullWidth />
      <DestinationAdAccountInput destination="microsoft-ads-conversion" />
    </>
  );
};

const openAiEventTypeChoices = OpenAiAdsEventTypes.map((t) => ({
  id: t,
  name: t,
}));

const OpenAiAdsDestinationConfig: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const eventType: OpenAiAdsEventType | undefined = useWatch({
    name: "destination.eventType",
  });

  return (
    <>
      <TextInput
        label="OpenAI Conversions API Key"
        source="destination.apiKey"
        type="password"
        helperText="From Ads Manager's Conversions tab. Not the Advertiser API key. Write-only — will show as masked when saved."
        fullWidth
      />
      <TextInput
        label="OpenAI Pixel ID"
        source="destination.pixelId"
        helperText="The data source's pixel_id, which a server-only setup also needs."
        fullWidth
      />
      <SelectInput
        label="OpenAI Event Type"
        source="destination.eventType"
        choices={openAiEventTypeChoices}
        helperText="OpenAI only reports these as campaign conversions once a conversion event setting for this pixel and event is attached to the campaign, and only from then on."
        required
        fullWidth
      />
      {eventType === "custom" && (
        <TextInput
          label="OpenAI Custom Event Name"
          source="destination.customEventName"
          helperText="Letters, digits, _ and -, starting and ending with a letter or digit."
          required
          fullWidth
        />
      )}
      <TextInput
        label="Currency"
        source="destination.currencyCode"
        helperText="ISO 4217 code of conversion values. Defaults to USD."
        fullWidth
      />
      <DestinationAdAccountInput destination="openai-ads" />
    </>
  );
};

const DestinationConfig: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const destinationName: ConversionOutputStreamDestination | undefined =
    useWatch({ name: "destination.name" });

  const configMap: Record<ConversionOutputStreamDestination, ReactElement> = {
    email: <EmailDestinationConfig />,
    webhook: <WebhookDestinationConfig />,
    "meta-ads": <MetaAdsDestinationConfig />,
    "google-ads-data-manager": <GoogleAdsDataManagerDestinationConfig />,
    "microsoft-ads-conversion": <MicrosoftAdsConversionDestinationConfig />,
    "openai-ads": <OpenAiAdsDestinationConfig />,
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Divider sx={{ pb: 2 }}>Destination Config</Divider>
      {configMap[destinationName ?? "email"] ?? <NoDestinationSet />}
    </Box>
  );
};

const RealtimeInput: React.FC = () => {
  return (
    <BooleanInput
      label="Realtime Processing"
      source="realtime"
      helperText="If this stream should process conversions as they arrive.  This is taxing on the system. If disabled, events are batched and processed periodically."
      fullWidth
    />
  );
};

/**
 * The stream's ad account by id alone, and none for a destination that
 * doesn't send to one (say, after switching destination).
 */
const adAccountId = (os: ConversionOutputStreamResource) => {
  const id = os.adAccount?.id;

  return outputStreamAdAccountPlatform(os.destination.name) && id
    ? { id }
    : null;
};

export const ConversionOutputStreamCreate = () => {
  return (
    <Create
      redirect="list"
      transform={(os: ConversionOutputStreamResource) => ({
        ...os,
        adAccount: adAccountId(os),
      })}
    >
      <SimpleForm>
        <FilterSelectInput />
        <TextInput source="description" fullWidth />
        <StartFromInput />
        <FromTimestampInput />
        <BooleanInput fullWidth source="enabled" />
        <RealtimeInput />
        <DestinationChooser />
        <DestinationConfig />
      </SimpleForm>
    </Create>
  );
};

export const ConversionOutputStreamEdit = () => {
  return (
    <Edit
      redirect="list"
      transform={(os: ConversionOutputStreamResource) => {
        return {
          ...omit(os, ["createdAt", "updatedAt"]),
          filter: { id: os.filter.id },
          adAccount: adAccountId(os),
          // Leave masked or blank write-only fields out so the backend keeps
          // their stored values.
          destination: omitUnchangedSensitiveFields(
            os.destination,
            getSensitiveFieldsForDestination(os.destination.name)
          ),
        };
      }}
    >
      <SimpleForm>
        <FilterSelectInput />
        <TextInput source="description" fullWidth />
        <StartFromInput />
        <FromTimestampInput />
        <BooleanInput fullWidth source="enabled" />
        <RealtimeInput />
        <DestinationChooser />
        <DestinationConfig />
      </SimpleForm>
    </Edit>
  );
};
